import React from "react";
import {
  Box,
  Stack,
  Paper,
  Group,
  Avatar,
  Text,
  TextInput,
  Button,
  ActionIcon,
  Card,
  Badge,
} from "@mantine/core";
import {
  IconSend,
  IconPlus,
  IconMenu2,
  IconClock,
  IconFileText,
  IconRobot,
  IconMessageChatbot,
  IconSparkles,
  IconGhost,
  IconChevronRight,
} from "@tabler/icons-react";

export default function ModernMinimalLayout({
  messages,
  inputValue,
  setInputValue,
  handleSend,
  handleItemSelect,
  messagesEndRef,
  currentStep,
  config,
  activeTab,
  setActiveTab,
  previousChats,
}: any) {
  const theme = config?.theme || {};
  const messaging = config?.messaging || {};

  const renderBotIcon = (size = 28) => {
    const iconType = config?.botAvatarIcon || "robot";
    const iconColor = config?.botAvatarIconColor || "#ffffff";
    switch (iconType) {
      case "chatbot":
        return <IconMessageChatbot size={size} color={iconColor} />;
      case "sparkles":
        return <IconSparkles size={size} color={iconColor} />;
      case "ghost":
        return <IconGhost size={size} color={iconColor} />;
      default:
        return <IconRobot size={size} color={iconColor} />;
    }
  };

  return (
    <Box
      style={{
        backgroundColor: theme.backgroundColor || "#f8f9fa",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Group
        position="apart"
        p="md"
        style={{
          backgroundColor: theme.primaryColor || "#1a1a1a",
          color: "#ffffff",
          boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
          zIndex: 10,
        }}
      >
        <Group spacing="sm">
          {config?.companyLogoUrl && (
            <Avatar src={config.companyLogoUrl} size="sm" radius="xs" />
          )}
          <Box>
            <Text weight={700} size="sm">
              {config?.companyName || "Concierge Support"}
            </Text>
            <Text size="xs" style={{ opacity: 0.8 }}>
              We usually reply instantly
            </Text>
          </Box>
        </Group>
        <ActionIcon
          variant="transparent"
          style={{ color: "#fff" }}
          onClick={() =>
            setActiveTab(activeTab === "current" ? "history" : "current")
          }
        >
          {activeTab === "current" ? (
            <IconMenu2 size={20} />
          ) : (
            <IconChevronRight size={20} />
          )}
        </ActionIcon>
      </Group>

      {/* Messages Area */}
      <Box style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        {activeTab === "history" ? (
          <Stack spacing="md">
            <Text weight={700} size="sm">
              Past Conversations
            </Text>
            {previousChats && previousChats.length > 0 ? (
              previousChats.map((chat: any, idx: number) => (
                <Card
                  key={idx}
                  withBorder
                  radius="md"
                  p="sm"
                  style={{
                    backgroundColor: "#fff",
                    borderColor: "rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <Group position="apart" mb="xs">
                    <Text size="xs" weight={700}>
                      {new Date(chat.created_at).toLocaleDateString()}
                    </Text>
                    <Badge size="sm" color="blue" variant="light">
                      {chat.status || "Follow-up"}
                    </Badge>
                  </Group>
                  <Text
                    size="xs"
                    color="dimmed"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {chat.description}
                  </Text>
                </Card>
              ))
            ) : (
              <Text size="xs" color="dimmed">
                No past conversations found for this number.
              </Text>
            )}
          </Stack>
        ) : (
          <Stack spacing="md">
            {/* Bot Greeting Header */}
            <Group align="flex-start" spacing="sm">
              <Avatar
                radius="xl"
                src={undefined}
                style={{
                  background:
                    config?.botAvatarBgColor ||
                    "linear-gradient(135deg, #111 0%, #333 100%)",
                }}
              >
                {renderBotIcon(24)}
              </Avatar>
              <Box>
                <Text weight={700} size="sm">
                  Hi, I'm Bench AI
                </Text>
                <Text size="xs" color="dimmed">
                  Your fitness & gym assistant
                </Text>
              </Box>
            </Group>

            {messages.map((msg: any) => (
              <Box key={msg.id}>
                {msg.sender === "user" ? (
                  <Paper
                    p="sm"
                    radius="xs" // Sharp edges for Concierge style
                    style={{
                      backgroundColor: messaging.userMessageBgColor || "#000",
                      color: messaging.userMessageTextColor || "#fff",
                      marginLeft: "auto",
                      maxWidth: "85%",
                      width: "fit-content",
                      marginTop: 8,
                      marginBottom: 8,
                      borderBottomRightRadius: 0, // Speech bubble effect
                    }}
                  >
                    <Text size="xs">{msg.text}</Text>
                    <Text
                      size="10px"
                      align="right"
                      style={{ opacity: 0.6 }}
                      mt={4}
                    >
                      {new Date().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                  </Paper>
                ) : (
                  <Group align="flex-start" spacing="sm" mt="sm">
                    <Avatar
                      radius="xl"
                      size="sm"
                      src={undefined}
                      style={{
                        background:
                          config?.botAvatarBgColor ||
                          "linear-gradient(135deg, #111 0%, #333 100%)",
                      }}
                    >
                      {renderBotIcon(16)}
                    </Avatar>
                    <Box style={{ width: "80%" }}>
                      <Paper
                        p="sm"
                        radius="xs" // Sharp edges
                        shadow="sm"
                        style={{
                          backgroundColor:
                            messaging.botMessageBgColor || "#fff",
                          color: messaging.botMessageTextColor || "#000",
                          borderBottomLeftRadius: 0, // Speech bubble effect
                        }}
                      >
                        <Text size="xs">{msg.text}</Text>

                        {/* Options */}
                        {msg.options && (
                          <Group
                            spacing="xs"
                            mt="sm"
                            style={{ flexWrap: "wrap" }}
                          >
                            {msg.options.map((opt: string) => (
                              <Button
                                key={opt}
                                variant="outline"
                                color="dark"
                                radius="xs" // Sharp edges
                                size="xs"
                                onClick={() => handleSend(opt)}
                                style={{
                                  height: "auto",
                                  minHeight: "30px",
                                  whiteSpace: "normal",
                                }}
                                styles={{
                                  label: {
                                    whiteSpace: "normal",
                                    textAlign: "left",
                                  },
                                }}
                              >
                                {opt}
                              </Button>
                            ))}
                          </Group>
                        )}

                        {/* List Items */}
                        {msg.listItems &&
                          msg.listItems.map((item: any, idx: number) => (
                            <Card
                              key={idx}
                              withBorder
                              radius="md"
                              p="xs"
                              mt="sm"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleItemSelect(item)}
                            >
                              <Group position="apart">
                                <Group spacing="xs">
                                  <ActionIcon variant="light" color="blue">
                                    <IconFileText size={16} />
                                  </ActionIcon>
                                  <Box>
                                    <Text size="xs" weight={700}>
                                      {item.title}
                                    </Text>
                                    <Text size="10px" color="dimmed">
                                      {item.subtitle}
                                    </Text>
                                  </Box>
                                </Group>
                                <IconChevronRight size={14} />
                              </Group>
                            </Card>
                          ))}
                      </Paper>
                      <Text size="10px" color="dimmed" mt={4}>
                        {new Date().toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Text>
                    </Box>
                  </Group>
                )}
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Stack>
        )}
      </Box>

      {/* Input Area */}
      {activeTab === "current" &&
        (currentStep === "ASK_CONTACT" ||
          currentStep === "ASK_ANYTHING_ELSE") && (
          <Group
            p="md"
            style={{ borderTop: "1px solid #eee", backgroundColor: "#fff" }}
          >
            <TextInput
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.currentTarget.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSend()}
              style={{ flex: 1 }}
              radius="xs"
              rightSection={
                <ActionIcon
                  onClick={() => handleSend()}
                  variant="filled"
                  radius="xs"
                  style={{ backgroundColor: theme.primaryColor || "#000" }}
                >
                  <IconSend size={14} />
                </ActionIcon>
              }
            />
          </Group>
        )}
    </Box>
  );
}
